<template>
  <div :style="themePageStyle">
    <body :data-sidebar="darkMode ? 'dark' : 'light'">
    <div id="layout-wrapper">
      <nav class="fs-topbar">
        <div class="container d-flex align-items-center justify-content-between">
          <a href="javascript:void(0)" @click="$router.back()" class="fs-back"><i class="mdi mdi-arrow-left me-1"></i>Back</a>
          <h6 class="m-0 fw-bold" style="color:var(--fm-h,#F5F0E8);">Feeding Schedules</h6>
          <button class="btn btn-sm btn-warning" @click="openAdd" :disabled="!farms.length"><i class="mdi mdi-plus me-1"></i>New</button>
        </div>
      </nav>

      <div class="container py-3">
        <div v-if="farms.length>1" class="mb-3">
          <label class="fs-label">Farm</label>
          <select v-model="farmId" class="fs-input w-100" @change="load"><option v-for="f in farms" :key="f.id" :value="f.id">{{ f.name }}</option></select>
        </div>

        <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
        <div v-else>
          <div class="fs-intro mb-3">
            <i class="mdi mdi-lightbulb-on-outline me-1" style="color:#BFA164;"></i>
            Recurring feeding plans for animals and batches. Link a formulation so the schedule carries a known daily cost.
          </div>
          <div v-for="s in schedules" :key="s.id" class="fs-card" :class="{'fs-inactive':Number(s.is_active)!==1}">
            <div class="fs-card-head">
              <div>
                <strong>{{ s.formulation_name || s.feed_type }}</strong>
                <span v-if="Number(s.is_active)!==1" class="fs-paused">paused</span>
                <div class="fs-meta">
                  <span><i class="mdi mdi-scale me-1"></i>{{ s.quantity_kg }}kg</span>
                  <span><i class="mdi mdi-repeat me-1"></i>{{ (s.frequency||'').replace(/_/g,' ') }}</span>
                  <span v-if="s.formulation_daily_cost>0"><i class="mdi mdi-cash me-1"></i>{{ Number(s.formulation_daily_cost).toLocaleString() }} Ksh/day</span>
                </div>
                <div class="fs-target"><i :class="s.animal_id?'mdi mdi-cow':'mdi mdi-group'" class="me-1"></i>{{ s.animal_name || s.animal_tag || s.batch_name || 'Farm-wide' }} · from {{ fDate(s.start_date) }}<span v-if="s.end_date"> to {{ fDate(s.end_date) }}</span></div>
              </div>
              <div class="d-flex align-items-center gap-1">
                <button class="fs-action" @click="toggle(s)" :title="Number(s.is_active)===1?'Pause':'Resume'"><i :class="Number(s.is_active)===1?'mdi mdi-pause-circle-outline':'mdi mdi-play-circle-outline'"></i></button>
                <button class="fs-action" @click="openEdit(s)" title="Edit"><i class="mdi mdi-pencil-outline"></i></button>
                <button class="fs-action fs-action-danger" @click="openDelete(s)" title="Delete"><i class="mdi mdi-trash-can-outline"></i></button>
              </div>
            </div>
            <div v-if="s.special_instructions" class="fs-instructions">{{ s.special_instructions }}</div>
          </div>
          <div v-if="schedules.length===0" class="fs-empty">
            <i class="mdi mdi-calendar-clock" style="font-size:36px;color:#6B6356;"></i>
            <p class="text-muted mt-2 mb-0">No feeding schedules for this farm yet. Tap <strong>New</strong> to add one.</p>
          </div>
        </div>
      </div>

      <!-- ADD/EDIT MODAL -->
      <div v-if="showModal" class="fs-overlay" @click.self="showModal=false"><div class="fs-modal">
        <h6 class="m-0 fw-bold mb-3" style="color:var(--fm-h,#F5F0E8);">{{ form.id ? 'Edit Schedule' : 'New Feeding Schedule' }}</h6>
        <div class="mb-2"><label class="fs-label">Applies to</label>
          <select v-model="form.target" class="fs-input w-100" :disabled="!!form.id">
            <option value="farm">Whole farm</option>
            <option value="animal">Specific animal</option>
            <option value="batch">Specific batch</option>
          </select>
        </div>
        <div class="mb-2" v-if="form.target==='animal'"><label class="fs-label">Animal</label>
          <select v-model="form.animalId" class="fs-input w-100"><option value="">Select animal…</option><option v-for="a in animals" :key="a.id" :value="a.id">{{ a.name||a.tag_number||'Unnamed' }} — {{ a.species_name }}</option></select>
        </div>
        <div class="mb-2" v-if="form.target==='batch'"><label class="fs-label">Batch</label>
          <select v-model="form.batchId" class="fs-input w-100"><option value="">Select batch…</option><option v-for="b in batches" :key="b.id" :value="b.id">{{ b.batch_name }}</option></select>
        </div>
        <div class="mb-2">
          <div class="d-flex justify-content-between align-items-end">
            <label class="fs-label m-0">Formulation</label>
            <a href="javascript:void(0)" @click="$router.push('/farm/feed-formulations')" style="color:#BFA164;font-size:11px;text-decoration:none;">Manage</a>
          </div>
          <select v-model="form.formulationId" class="fs-input w-100" @change="onFormulationPicked">
            <option value="">Free-form (type below)</option>
            <option v-for="f in formulations" :key="f.id" :value="f.id">{{ f.name }} — {{ Number(f.daily_cost_kshs).toLocaleString() }} Ksh/day</option>
          </select>
        </div>
        <div class="row g-2 mb-2">
          <div class="col-7"><label class="fs-label">Feed Type *</label><input v-model="form.feedType" class="fs-input w-100" :readonly="!!form.formulationId" /></div>
          <div class="col-5"><label class="fs-label">Qty (kg) *</label><input v-model="form.quantityKg" class="fs-input w-100" type="number" step="0.1" :readonly="!!form.formulationId" /></div>
        </div>
        <div class="mb-2"><label class="fs-label">Frequency</label>
          <select v-model="form.frequency" class="fs-input w-100">
            <option value="once_daily">Once daily</option><option value="twice_daily">Twice daily</option><option value="three_times">Three times</option><option value="four_times">Four times</option><option value="ad_libitum">Ad libitum</option><option value="custom">Custom</option>
          </select>
        </div>
        <div class="row g-2 mb-2">
          <div class="col-6"><label class="fs-label">Start *</label><input v-model="form.startDate" class="fs-input w-100" type="date" /></div>
          <div class="col-6"><label class="fs-label">End</label><input v-model="form.endDate" class="fs-input w-100" type="date" /></div>
        </div>
        <div class="mb-3"><label class="fs-label">Special Instructions</label><textarea v-model="form.specialInstructions" class="fs-input w-100" rows="2"></textarea></div>
        <button class="btn btn-warning w-100" @click="save" :disabled="!canSave||saving"><span v-if="saving"><span class="spinner-border spinner-border-sm me-1"></span>Saving…</span><span v-else>{{ form.id ? 'Save Changes' : 'Create Schedule' }}</span></button>
        <button class="btn btn-sm btn-link text-muted w-100 mt-1" @click="showModal=false">Cancel</button>
      </div></div>

      <!-- DELETE CONFIRM -->
      <div v-if="showDelete" class="fs-overlay" @click.self="showDelete=false"><div class="fs-modal fs-modal-danger">
        <div class="fs-del-icon"><i class="mdi mdi-alert-outline"></i></div>
        <h6 class="fs-del-title">Delete feeding schedule?</h6>
        <div v-if="delTarget" class="fs-del-summary"><strong>{{ delTarget.formulation_name || delTarget.feed_type }}</strong><span>{{ delTarget.quantity_kg }}kg · {{ (delTarget.frequency||'').replace(/_/g,' ') }}</span></div>
        <p class="fs-del-warn">This permanently removes the recurring plan. Logged feeds are not affected.</p>
        <label class="fs-label">Type <strong style="color:#FF6B6B;">DELETE</strong> to confirm</label>
        <input v-model="delText" class="fs-input w-100" placeholder="DELETE" @keyup.enter="confirmDelete" />
        <button class="btn w-100 mt-3 fs-del-btn" :disabled="delText.trim().toUpperCase()!=='DELETE'||saving" @click="confirmDelete"><span v-if="saving"><span class="spinner-border spinner-border-sm me-1"></span>Deleting…</span><span v-else>Delete schedule</span></button>
        <button class="btn btn-sm btn-link text-muted w-100 mt-1" @click="showDelete=false">Cancel</button>
      </div></div>
    </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";
export default {
  name: "FeedingSchedules",
  data() {
    return {
      darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
      loading:true, saving:false,
      farms:[], farmId:'', schedules:[], formulations:[], animals:[], batches:[],
      showModal:false, form:{ id:null, target:'farm', animalId:'', batchId:'', formulationId:'', feedType:'', quantityKg:'', frequency:'twice_daily', startDate:new Date().toISOString().split('T')[0], endDate:'', specialInstructions:'' },
      showDelete:false, delTarget:null, delText:''
    };
  },
  computed: {
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    canSave(){return !!this.form.feedType && !!this.form.quantityKg && !!this.form.startDate && (this.form.target!=='animal'||this.form.animalId) && (this.form.target!=='batch'||this.form.batchId)}
  },
  mounted(){ this.init(); },
  methods: {
    fDate(d){if(!d)return'';return new Date(d).toLocaleDateString('en-KE',{day:'numeric',month:'short',year:'2-digit'})},
    async init(){
      var uid=sessionStorage.getItem('userId')||'';
      try{
        var r=await executeJson({function:'getMyFarms',ownerId:uid},flocker);
        if(r.data&&r.data.success)this.farms=r.data.data.farms||[];
        if(this.farms.length)this.farmId=this.farms[0].id;
        var fr=await executeJson({function:'getFeedFormulations',ownerId:uid},flocker);
        if(fr.data&&fr.data.success)this.formulations=fr.data.data.formulations||[];
      }catch(e){console.error(e)}
      await this.load();
    },
    async load(){
      this.loading=true;
      if(this.farmId){
        try{
          var r=await executeJson({function:'getFeedingSchedules',farmId:this.farmId},flocker);
          if(r.data&&r.data.success)this.schedules=r.data.data.schedules||[];
          var ar=await executeJson({function:'getFarmAnimals',farmId:this.farmId},flocker).catch(function(){return null});
          if(ar&&ar.data&&ar.data.success)this.animals=ar.data.data.animals||ar.data.data||[];
          var br=await executeJson({function:'getFarmBatches',farmId:this.farmId},flocker).catch(function(){return null});
          if(br&&br.data&&br.data.success)this.batches=br.data.data.batches||[];
        }catch(e){console.error(e)}
      }
      this.loading=false;
    },
    onFormulationPicked(){
      if(!this.form.formulationId)return;
      var self=this;var f=this.formulations.find(function(x){return String(x.id)===String(self.form.formulationId)});
      if(f){this.form.feedType=f.name;
        executeJson({function:'getFeedFormulation',formulationId:f.id},flocker).then(function(r){
          if(r.data&&r.data.success){var q=0;(r.data.data.items||[]).forEach(function(it){q+=Number(it.quantity_kg||0)});self.form.quantityKg=q.toFixed(2)}
        });
      }
    },
    openAdd(){this.form={id:null,target:'farm',animalId:'',batchId:'',formulationId:'',feedType:'',quantityKg:'',frequency:'twice_daily',startDate:new Date().toISOString().split('T')[0],endDate:'',specialInstructions:''};this.showModal=true},
    openEdit(s){
      this.form={id:s.id,target:s.animal_id?'animal':(s.batch_id?'batch':'farm'),animalId:s.animal_id||'',batchId:s.batch_id||'',formulationId:s.formulation_id||'',feedType:s.feed_type||'',quantityKg:s.quantity_kg||'',frequency:s.frequency||'twice_daily',startDate:(s.start_date||'').split(' ')[0].split('T')[0]||'',endDate:(s.end_date||'').split(' ')[0].split('T')[0]||'',specialInstructions:s.special_instructions||''};
      this.showModal=true;
    },
    async save(){
      this.saving=true;
      var uid=sessionStorage.getItem('userId')||'';
      try{
        var p;
        if(this.form.id){
          p={function:'updateFeedingSchedule',scheduleId:this.form.id,formulationId:this.form.formulationId,feedType:this.form.feedType,quantityKg:this.form.quantityKg,frequency:this.form.frequency,startDate:this.form.startDate,endDate:this.form.endDate,specialInstructions:this.form.specialInstructions};
        } else {
          p={function:'addFeedingSchedule',farmId:this.farmId,formulationId:this.form.formulationId,feedType:this.form.feedType,quantityKg:this.form.quantityKg,frequency:this.form.frequency,startDate:this.form.startDate,endDate:this.form.endDate,specialInstructions:this.form.specialInstructions,createdBy:uid};
          if(this.form.target==='animal')p.animalId=this.form.animalId;
          if(this.form.target==='batch')p.batchId=this.form.batchId;
        }
        var r=await executeJson(p,flocker);
        if(r.data&&r.data.success){this.showModal=false;await this.load()}else alert(r.data.message||'Failed');
      }catch(e){alert('Error')}
      this.saving=false;
    },
    async toggle(s){
      try{var r=await executeJson({function:'updateFeedingSchedule',scheduleId:s.id,isActive:Number(s.is_active)===1?0:1},flocker);
        if(r.data&&r.data.success)await this.load()}catch(e){}
    },
    openDelete(s){this.delTarget=s;this.delText='';this.showDelete=true},
    async confirmDelete(){
      if(!this.delTarget||this.delText.trim().toUpperCase()!=='DELETE')return;
      this.saving=true;
      try{var r=await executeJson({function:'deleteFeedingSchedule',scheduleId:this.delTarget.id},flocker);
        if(r.data&&r.data.success){this.showDelete=false;this.delTarget=null;await this.load()}else alert(r.data.message||'Failed')}catch(e){alert('Error')}
      this.saving=false;
    }
  }
};
</script>

<style scoped>
.fs-topbar{background:var(--fm-bg1,#141414);border-bottom:1px solid var(--fm-br,#1E1E1E);padding:12px 0;position:sticky;top:0;z-index:10}
html[data-theme="light"] .fs-topbar{background:#FFFFFF!important;border-color:#E8E2DA!important}
.fs-back{color:#BFA164;text-decoration:none;font-size:13px;font-weight:600}
.fs-intro{background:rgba(191,161,100,.06);border:1px solid rgba(191,161,100,.18);border-radius:8px;padding:10px 12px;font-size:12px;color:var(--fm-m,#A09888)}
.fs-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:12px;margin-bottom:8px}
html[data-theme="light"] .fs-card{background:#FFFFFF!important;border-color:#E8E2DA!important}
.fs-inactive{opacity:.6}
.fs-card-head{display:flex;justify-content:space-between;align-items:flex-start;gap:10px}
.fs-card-head strong{color:var(--fm-h,#F5F0E8);font-size:14px}
.fs-paused{font-size:10px;color:#FF9800;background:rgba(255,152,0,.12);padding:1px 6px;border-radius:4px;margin-left:6px}
.fs-meta{display:flex;gap:10px;flex-wrap:wrap;font-size:11px;color:var(--fm-m,#A09888);margin-top:4px}
.fs-target{font-size:11px;color:var(--fm-s,#6B6356);margin-top:4px}
.fs-instructions{font-size:11px;color:var(--fm-s,#6B6356);margin-top:6px;border-top:1px solid var(--fm-br,#1E1E1E);padding-top:6px}
.fs-action{background:transparent;border:none;color:var(--fm-s,#6B6356);cursor:pointer;padding:4px 6px;border-radius:6px;font-size:16px;transition:all .15s}
.fs-action:hover{background:rgba(191,161,100,.12);color:#BFA164}
.fs-action-danger:hover{background:rgba(255,107,107,.12);color:#FF6B6B}
.fs-empty{text-align:center;padding:32px 16px;background:var(--fm-bg1,#141414);border:1px dashed var(--fm-br,#2A2520);border-radius:10px}
html[data-theme="light"] .fs-empty{background:#FFFFFF!important}
.fs-overlay{position:fixed;inset:0;background:rgba(0,0,0,.7);z-index:9999;display:flex;justify-content:center;align-items:flex-start;padding:24px;overflow-y:auto}
.fs-modal{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#2A2520);border-radius:12px;padding:20px;width:100%;max-width:480px;max-height:calc(100vh - 48px);overflow-y:auto}
html[data-theme="light"] .fs-modal{background:#FFFFFF!important;border-color:#E8E2DA!important}
.fs-label{display:block;font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;letter-spacing:.3px;margin-bottom:4px}
.fs-input{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#2A2520);color:var(--fm-h,#F5F0E8);border-radius:6px;padding:8px 10px;font-size:13px;width:100%}
html[data-theme="light"] .fs-input{background:#F7F5F2!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
.fs-modal-danger{text-align:center;max-width:360px}
.fs-del-icon{width:56px;height:56px;border-radius:50%;background:rgba(255,107,107,.12);display:flex;align-items:center;justify-content:center;margin:0 auto 12px}
.fs-del-icon i{font-size:30px;color:#FF6B6B}
.fs-del-title{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:16px;margin-bottom:10px}
.fs-del-summary{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#2A2520);border-radius:8px;padding:10px;margin-bottom:12px;display:flex;flex-direction:column;gap:2px}
.fs-del-summary strong{color:var(--fm-h,#F5F0E8)}
.fs-del-summary span{color:var(--fm-s,#6B6356);font-size:12px}
.fs-del-warn{color:var(--fm-m,#A09888);font-size:12px;line-height:1.5;margin-bottom:14px;text-align:left}
.fs-del-btn{background:#FF6B6B;border:none;color:#fff;font-weight:600}
.fs-del-btn:disabled{background:rgba(255,107,107,.3);color:rgba(255,255,255,.6);cursor:not-allowed}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .fs-container { padding-top: 60px !important; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; padding-left: 12px !important; padding-right: 12px !important; }
  .fs-card { padding: 12px !important; border-radius: 10px !important; }
  .fs-input { font-size: 16px !important; }
  .fs-del-summary span { font-size: 11px; }
  .fs-del-warn { font-size: 11px; }
}
</style>
